/**
 * Smart Personalization Engine — Duda-style dynamic content swapping.
 *
 * Detects four signals on the client (zero PII, no IP lookup, no third-party
 * services) and exposes them via the `usePersonalization()` hook so any
 * component can pick variants of hero copy, images, CTAs, or whole layout
 * sections to maximize conversion.
 *
 * Signals:
 *   1. location       — IANA timezone → Canadian province (existing behaviour)
 *   2. device         — viewport-based 'mobile' | 'tablet' | 'desktop'
 *   3. visitor        — localStorage-tracked 'first_time' | 'returning' | 'engaged'
 *   4. referrer       — document.referrer host → 'google' | 'linkedin' |
 *                       'facebook' | 'twitter' | 'direct' | 'other'
 *   5. time_of_day    — local hour bucket
 *
 * Visitor state is persisted in localStorage under `cr_visitor_meta`:
 *   { visits: number, first_seen: iso, last_seen: iso, max_scroll: 0-100 }
 * After 3 visits OR 60s+ scroll-past-50% on first visit → 'engaged'.
 */
import { useEffect, useState } from "react";

const META_KEY = "cr_visitor_meta";
const AUDIENCE_KEY = "cr_audience";

// Audience signals — keep tags in sync with utm_audience values we emit
// from our own landing pages (/lp/landlord, /lp/tenant) and the
// Performance Max asset feed.
const AUDIENCES = {
  PMC: "pmc",
  INDIVIDUAL_LANDLORD: "individual_landlord",
  TENANT: "tenant",
  UNKNOWN: "unknown",
};
const VALID_AUDIENCES = new Set(Object.values(AUDIENCES));

const TZ_TO_PROVINCE = {
  "America/Toronto":     { code: "ON", name: "Ontario",                 slug: "ontario",                 city: "Toronto" },
  "America/Montreal":    { code: "QC", name: "Quebec",                  slug: "quebec",                  city: "Montreal" },
  "America/Vancouver":   { code: "BC", name: "British Columbia",        slug: "british-columbia",        city: "Vancouver" },
  "America/Edmonton":    { code: "AB", name: "Alberta",                 slug: "alberta",                 city: "Edmonton" },
  "America/Calgary":     { code: "AB", name: "Alberta",                 slug: "alberta",                 city: "Calgary" },
  "America/Winnipeg":    { code: "MB", name: "Manitoba",                slug: "manitoba",                city: "Winnipeg" },
  "America/Regina":      { code: "SK", name: "Saskatchewan",            slug: "saskatchewan",            city: "Regina" },
  "America/Halifax":     { code: "NS", name: "Nova Scotia",             slug: "nova-scotia",             city: "Halifax" },
  "America/Moncton":     { code: "NB", name: "New Brunswick",           slug: "new-brunswick",           city: "Moncton" },
  "America/St_Johns":    { code: "NL", name: "Newfoundland & Labrador", slug: "newfoundland-and-labrador", city: "St. John's" },
  "America/Whitehorse":  { code: "YT", name: "Yukon",                   slug: "yukon",                   city: "Whitehorse" },
  "America/Yellowknife": { code: "NT", name: "Northwest Territories",   slug: "northwest-territories",   city: "Yellowknife" },
  "America/Iqaluit":     { code: "NU", name: "Nunavut",                 slug: "nunavut",                 city: "Iqaluit" },
};

const REFERRER_MAP = [
  { host: /google\./i,         tag: "google" },
  { host: /linkedin\./i,       tag: "linkedin" },
  { host: /facebook\.|fb\./i,  tag: "facebook" },
  { host: /(twitter|x\.com)/i, tag: "twitter" },
  { host: /bing\./i,           tag: "bing" },
  { host: /duckduckgo\./i,     tag: "duckduckgo" },
];

function detectProvince() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_TO_PROVINCE[tz] || null;
  } catch {
    return null;
  }
}

function detectDevice() {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth || 1024;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function detectReferrer() {
  if (typeof document === "undefined") return "direct";
  const ref = document.referrer || "";
  if (!ref) return "direct";
  for (const { host, tag } of REFERRER_MAP) if (host.test(ref)) return tag;
  return "other";
}

function detectTimeOfDay() {
  const h = new Date().getHours();
  if (h < 6) return "night";
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  if (h < 21) return "evening";
  return "night";
}

function loadMeta() {
  if (typeof localStorage === "undefined") return null;
  try { return JSON.parse(localStorage.getItem(META_KEY) || "null"); } catch { return null; }
}

function saveMeta(meta) {
  try { localStorage.setItem(META_KEY, JSON.stringify(meta)); } catch { /* noop */ }
}

/**
 * Bump the visit counter on every fresh page-view (debounced to once per
 * session via a sessionStorage flag so SPA navigations don't inflate it).
 */
function bumpVisit() {
  if (typeof sessionStorage === "undefined") return loadMeta();
  if (sessionStorage.getItem("cr_visit_counted") === "1") return loadMeta();
  const now = new Date().toISOString();
  const meta = loadMeta() || { visits: 0, first_seen: now, last_seen: now, max_scroll: 0 };
  meta.visits = (meta.visits || 0) + 1;
  meta.last_seen = now;
  saveMeta(meta);
  sessionStorage.setItem("cr_visit_counted", "1");
  return meta;
}

function classifyVisitor(meta) {
  if (!meta || (meta.visits || 0) <= 1) return "first_time";
  if ((meta.visits || 0) >= 3 || (meta.max_scroll || 0) >= 50) return "engaged";
  return "returning";
}

// ─────────────────────────── Audience detection ──────────────────────────
//
// Cascade (highest signal first). The first hit wins. Sticky in
// localStorage so SPA navigations don't bounce the audience.
//
//   1. ?utm_audience=pmc|landlord|tenant            (explicit signal — wins)
//   2. utm_campaign contains pmc/enterprise/yardi   → PMC
//      utm_campaign contains landlord/indie/single  → individual_landlord
//      utm_campaign contains tenant                 → tenant
//   3. Path-based: /vs/{buildium|yardi|appfolio|propertyware} → PMC
//                  /lp/tenant or /tenants           → tenant
//                  /tools/* or /lp/landlord         → individual_landlord
//   4. Referrer host:  linkedin.com                  → PMC
//                      facebook/instagram            → individual_landlord
//   5. Engagement: growth-calculator units ≥ 50      → PMC (sticky)
//                  growth-calculator units ≤ 25      → individual_landlord
//
function readAudienceFromStorage() {
  if (typeof localStorage === "undefined") return null;
  try {
    const v = localStorage.getItem(AUDIENCE_KEY);
    return v && VALID_AUDIENCES.has(v) ? v : null;
  } catch {
    return null;
  }
}

function writeAudienceToStorage(audience) {
  try {
    if (VALID_AUDIENCES.has(audience)) localStorage.setItem(AUDIENCE_KEY, audience);
  } catch { /* noop */ }
}

function detectAudienceFromQuery() {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  const explicit = (q.get("utm_audience") || q.get("audience") || "").toLowerCase();
  if (explicit === "pmc" || explicit === "enterprise") return AUDIENCES.PMC;
  if (explicit === "landlord" || explicit === "individual_landlord" || explicit === "indie") return AUDIENCES.INDIVIDUAL_LANDLORD;
  if (explicit === "tenant") return AUDIENCES.TENANT;
  const campaign = (q.get("utm_campaign") || "").toLowerCase();
  if (/(pmc|enterprise|yardi|buildium|appfolio|propertyware|manager)/.test(campaign)) return AUDIENCES.PMC;
  if (/(tenant|renter)/.test(campaign)) return AUDIENCES.TENANT;
  if (/(landlord|indie|single|small)/.test(campaign)) return AUDIENCES.INDIVIDUAL_LANDLORD;
  return null;
}

function detectAudienceFromPath() {
  if (typeof window === "undefined") return null;
  const p = (window.location.pathname || "").toLowerCase();
  if (/^\/vs\/(buildium|yardi|appfolio|propertyware|rentmanager|onesite)/.test(p)) return AUDIENCES.PMC;
  if (p.startsWith("/lp/tenant") || p.startsWith("/tenants")) return AUDIENCES.TENANT;
  if (p.startsWith("/lp/landlord") || p.startsWith("/tools")) return AUDIENCES.INDIVIDUAL_LANDLORD;
  return null;
}

function detectAudienceFromReferrer(refTag) {
  if (refTag === "linkedin") return AUDIENCES.PMC;
  if (refTag === "facebook") return AUDIENCES.INDIVIDUAL_LANDLORD;
  return null;
}

export function detectAudience({ referrer } = {}) {
  // Explicit URL params win — always overwrite sticky.
  const fromQuery = detectAudienceFromQuery();
  if (fromQuery) { writeAudienceToStorage(fromQuery); return fromQuery; }

  // Sticky next — most reliable for repeat sessions.
  const sticky = readAudienceFromStorage();
  if (sticky) return sticky;

  const fromPath = detectAudienceFromPath();
  if (fromPath) { writeAudienceToStorage(fromPath); return fromPath; }

  const fromRef = detectAudienceFromReferrer(referrer);
  if (fromRef) { writeAudienceToStorage(fromRef); return fromRef; }

  return AUDIENCES.UNKNOWN;
}

/** Called by engagement components (Growth Calculator, etc.) to refine
 *  the audience signal once they collect data. */
export function setAudience(audience) {
  if (!VALID_AUDIENCES.has(audience)) return;
  writeAudienceToStorage(audience);
  try {
    window.dispatchEvent(new CustomEvent("cr:audience-change", { detail: { audience } }));
  } catch { /* noop */ }
}


/**
 * usePersonalization() — single hook returning the full snapshot.
 * Safe to call from any component; reads are all client-side and
 * synchronous (no network), so there's no flash-of-wrong-content.
 */
export function usePersonalization() {
  // Personalization disabled — returns a fixed default state so every
  // visitor sees the same hero copy. Re-enable by restoring the dynamic
  // version of this hook in /app/frontend/src/lib/personalizer.js.
  const [state] = useState(() => ({
    province: null,
    device: "desktop",
    referrer: "direct",
    time_of_day: "afternoon",
    visitor: "first_time",
    audience: "unknown",
    meta: null,
  }));
  return state;
}

/* Personalization is disabled — the original dynamic implementation is
 * preserved below as a comment for future reference. To re-enable, restore
 * the export above to use this logic. */
// function _disabledUsePersonalization() { ... original dynamic hook code removed to fix production build; recover from git history if needed. }

// Headline/subhead/CTA variant copy keyed by signal. Components import
// `pickVariant()` to read a string by key with a sensible fallback.
export const VARIANTS = {
  heroSubject: {
    // Hero H1 must read "Canadian landlord" nationwide — province-level
    // personalization is handled by the smaller chip / nudge instead so
    // the brand still feels country-scale on first paint. (Decision: 2026-06-22)
    base: "Canadian landlord.",
    byProvince: () => "Canadian landlord.",
  },
  heroSubheadByReferrer: {
    google:     "Collect rent, screen applicants, generate lease documents, manage maintenance and report at tax time — all in one place.",
    linkedin:   "Recommended by Canadian landlords on LinkedIn — collect rent, build compliant leases, and own your tax exports.",
    facebook:   "Trusted by Canadian landlord communities — rent collection, compliant leases, maintenance and tax exports.",
    twitter:    "The Canadian property OS people are talking about — rent, leases, maintenance, and CRA-ready exports.",
    direct:     "Collect rent, screen applicants, generate lease documents, manage maintenance and report at tax time — all in one place.",
    bing:       "Collect rent, screen applicants, generate lease documents, manage maintenance and report at tax time — all in one place.",
    duckduckgo: "Privacy-first Canadian landlord software — rent, leases, maintenance, and tax exports without leaking your tenants' data.",
    other:      "Collect rent, screen applicants, generate lease documents, manage maintenance and report at tax time — all in one place.",
  },
  primaryCta: {
    first_time: { label: "Choose a plan", to: "/signup?role=landlord" },
    returning:  { label: "Continue setup", to: "/signup?role=landlord" },
    engaged:    { label: "Claim your trial — Paid plans from CA$35/month", to: "/signup?role=landlord" },
  },
  greetingByTime: {
    morning:   "Good morning",
    afternoon: "Good afternoon",
    evening:   "Good evening",
    night:     "Burning the midnight oil?",
  },
  // ─────────────────────── Audience-keyed variants ───────────────────────
  // Used by Landing.jsx + LP pages. Tenant variant deliberately points
  // away from the landlord trial CTA. Unknown → individual_landlord
  // default (highest-volume audience).
  heroEyebrowByAudience: {
    pmc:                  "For property management companies",
    individual_landlord:  "For Canadian landlords",
    tenant:               "For renters",
    unknown:              "For Canadian landlords",
  },
  heroSubheadByAudience: {
    pmc:                  "Replace Buildium, Yardi or AppFolio with the Canadian-built OS — T776 + NR4 exports, 13-province leases, CAD rent via Stripe, and Mark handling the busywork across hundreds of doors.",
    individual_landlord:  "Collect rent, screen applicants, generate lease documents, manage maintenance and report at tax time — all in one place.",
    tenant:               "Pay rent, message your landlord, submit maintenance, and build credit history — all from one place.",
    unknown:              "Collect rent, screen applicants, generate lease documents, manage maintenance and report at tax time — all in one place.",
  },
  primaryCtaByAudience: {
    pmc:                  { label: "Book a 15-min demo", to: "/get-started", testid: "hero-cta-demo" },
    individual_landlord:  { label: "Choose a plan", to: "/signup?role=landlord", testid: "hero-cta-trial" },
    tenant:               { label: "Find a place to rent",  to: "/properties", testid: "hero-cta-rent" },
    unknown:              { label: "Choose a plan", to: "/signup?role=landlord", testid: "hero-cta-trial" },
  },
  copilotHeadlineByAudience: {
    pmc:                  "Run hundreds of doors with the team you already have.",
    individual_landlord:  "Scale to a property management company — overnight, solo.",
    tenant:               "Your home, your messages, your maintenance — in one place.",
    unknown:              "Scale to a property management company — overnight, solo.",
  },
  copilotSubheadByAudience: {
    pmc:                  "Mark sits beside your existing ops team and absorbs the work that doesn't need a human — tenant replies, vendor coordination, lease renewals, ledger reconciliation, and CRA-ready reports. Reclaim 30+ hours per portfolio manager every week.",
    individual_landlord:  "Mark is built into every Central Rentals account. Inside one landlord portal he runs the whole show — the kind of operation that used to need an office of five — while you stay in your day job.",
    tenant:               "Mark answers your questions about your rent, your lease, and your unit at any hour — no app to download, no portal to log into.",
    unknown:              "Mark is built into every Central Rentals account. Inside one landlord portal he runs the whole show — the kind of operation that used to need an office of five — while you stay in your day job.",
  },
};

export function pickHeroSubject(p) {
  // Daily-rotating hero subject: SEO-fresh homepage top-line every day of
  // the year, with a province-aware anchor when we can detect it. Deterministic
  // per day (same for every visitor on the same date) so crawlers see stable
  // content within a 24-hour window.
  const daily = pickDailyHeroSubject(p);
  if (daily) return daily;
  return p.province ? VARIANTS.heroSubject.byProvince(p.province) : VARIANTS.heroSubject.base;
}

const DAILY_HERO_POOL = [
  "coast-to-coast Canadian landlord",
  "small-portfolio Canadian landlord",
  "duplex owner in every province",
  "condo landlord who does it themselves",
  "basement-suite landlord next door",
  "triplex owner running lean",
  "rental-suite hobbyist going pro",
  "portfolio you built one door at a time",
  "landlord who wants nights + weekends back",
  "landlord who hates spreadsheets",
  "landlord filing T776 without an accountant",
  "landlord juggling day job + tenants",
  "family-run rental business",
  "landlord tired of paying property managers",
  "landlord who wants receipts, not surprises",
  "landlord managing from their kitchen table",
  "landlord finally getting organized",
  "landlord who wants CRA-ready books",
  "landlord who says yes to good tenants faster",
  "landlord who wants their weekends back",
  "landlord replacing five apps with one",
  "landlord who wants rent in on the 1st",
  "landlord who wants leases signed today",
  "landlord scaling from 1 door to 20",
  "Canadian owner-operator",
  "landlord who wants clarity, not chaos",
  "landlord who runs it like a business",
  "landlord who wants their books CRA-ready",
  "landlord who's done chasing rent",
  "landlord tired of paper leases",
  "landlord who wants everything in one place",
];

function _dayOfYear(d) {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d - start + (start.getTimezoneOffset() - d.getTimezoneOffset()) * 60 * 1000;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export function pickDailyHeroSubject(_p) {
  try {
    const idx = _dayOfYear(new Date()) % DAILY_HERO_POOL.length;
    return DAILY_HERO_POOL[idx];
  } catch (_e) {
    return null;
  }
}

export function pickHeroSubhead(p) {
  return VARIANTS.heroSubheadByReferrer[p.referrer] || VARIANTS.heroSubheadByReferrer.direct;
}

export function pickPrimaryCta(p) {
  return VARIANTS.primaryCta[p.visitor] || VARIANTS.primaryCta.first_time;
}

export function pickGreeting(p) {
  return VARIANTS.greetingByTime[p.time_of_day] || "Hello";
}

export function pickByAudience(map, audience) {
  return map[audience] || map.unknown || map.individual_landlord;
}
export function pickAudienceEyebrow(p)   { return pickByAudience(VARIANTS.heroEyebrowByAudience,   p.audience); }
export function pickAudienceSubhead(p)   { return pickByAudience(VARIANTS.heroSubheadByAudience,   p.audience); }
export function pickAudienceCta(p)       { return pickByAudience(VARIANTS.primaryCtaByAudience,    p.audience); }
export function pickCopilotHeadline(p)   { return pickByAudience(VARIANTS.copilotHeadlineByAudience, p.audience); }
export function pickCopilotSubhead(p)    { return pickByAudience(VARIANTS.copilotSubheadByAudience,  p.audience); }

// Re-export for legacy callers that still import `detectProvince`.
export { detectProvince, AUDIENCES };


/* ─────────────────────────── V2 helpers (design agent) ────────────────
 * Deterministic role/portfolio/province personalization for the new
 * V2 dashboards. Each helper takes a user object + a translator so the
 * caller can pass `useT()` from lib/i18n.jsx.
 *
 *   getPersonalizedGreeting(user, t)
 *   getLegalCallout(user, t)
 *   getRoleBasedCTA(user, t)
 *
 * Where `t(key, fallback)` returns the FR string when the language
 * dictionary contains the key, else falls back to English. Kept
 * side-effect-free so both server-rendered and client-only pages can
 * use them.
 * ──────────────────────────────────────────────────────────────────── */

export function getPersonalizedGreeting(user, t = (_k, f) => f) {
  if (!user) return t("v2.greet_guest", "Welcome to Central Rentals.");
  const name = user.first_name || (user.name || "").split(" ")[0] || "";
  if (user.role === "landlord") {
    if ((user.visit_count || 0) <= 1) {
      return t("v2.greet_landlord_first", `Welcome${name ? `, ${name}` : ""}. Let's set up your first property.`);
    }
    if ((user.portfolio_size || user.unit_count || 0) > 10) {
      return t("v2.greet_landlord_large", `Welcome back${name ? `, ${name}` : ""}. Your ${user.portfolio_size || user.unit_count} units are performing well.`);
    }
    return t("v2.greet_landlord_return", `Welcome back${name ? `, ${name}` : ""}. Here is today's briefing.`);
  }
  if (user.role === "tenant") {
    if ((user.visit_count || 0) <= 1) {
      return t("v2.greet_tenant_first", `Welcome home${name ? `, ${name}` : ""}. Set up your rent payments and explore the community.`);
    }
    return t("v2.greet_tenant_return", `Welcome back${name ? `, ${name}` : ""}.`);
  }
  return t("v2.greet_generic", "Welcome to Central Rentals.");
}

export function getLegalCallout(user, t = (_k, f) => f) {
  const prov = user?.province || "";
  if (prov === "QC") return t("v2.legal_qc", "Loi 41 + TAL guidelines apply. Bail bilingue is generated automatically.");
  if (prov === "ON") return t("v2.legal_on", "LTB rules apply. The standard Ontario Lease is generated automatically.");
  if (prov === "BC") return t("v2.legal_bc", "RTB rules apply. Provincial notice templates are pre-filled.");
  if (prov === "AB") return t("v2.legal_ab", "RTDRS rules apply. Alberta security-deposit interest is auto-computed.");
  return t("v2.legal_default", "Provincial tenancy rules are applied automatically.");
}

export function getRoleBasedCTA(user, t = (_k, f) => f) {
  if (user?.role === "landlord") return t("v2.cta_add_property", "Add a property");
  if (user?.role === "tenant") return t("v2.cta_pay_rent", "Pay rent");
  return t("v2.cta_start_trial", "Choose a plan");
}
